import React, { createContext, useContext, useReducer } from 'react';

// Initial state
const initialState = {
  selectedPlace: null,
  gameSession: null,
  userPreferences: {
    ageGroup: 'all',
    difficultyLevel: 3,
    language: 'en'
  },
  searchQuery: '',
  filters: {
    category: '',
    country: '',
    difficulty: null
  },
  gameProgress: {},
  achievements: []
};

// Action types
const actionTypes = {
  SET_SELECTED_PLACE: 'SET_SELECTED_PLACE',
  SET_GAME_SESSION: 'SET_GAME_SESSION',
  UPDATE_USER_PREFERENCES: 'UPDATE_USER_PREFERENCES',
  SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
  SET_FILTERS: 'SET_FILTERS',
  UPDATE_GAME_PROGRESS: 'UPDATE_GAME_PROGRESS',
  ADD_ACHIEVEMENT: 'ADD_ACHIEVEMENT',
  RESET_STATE: 'RESET_STATE'
};

// Reducer function
function travelReducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_SELECTED_PLACE:
      return {
        ...state,
        selectedPlace: action.payload
      };

    case actionTypes.SET_GAME_SESSION:
      return {
        ...state,
        gameSession: action.payload
      };

    case actionTypes.UPDATE_USER_PREFERENCES:
      return {
        ...state,
        userPreferences: {
          ...state.userPreferences,
          ...action.payload
        }
      };

    case actionTypes.SET_SEARCH_QUERY:
      return {
        ...state,
        searchQuery: action.payload
      };

    case actionTypes.SET_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload
        }
      };

    case actionTypes.UPDATE_GAME_PROGRESS:
      return {
        ...state,
        gameProgress: {
          ...state.gameProgress,
          [action.payload.gameId]: action.payload.progress
        }
      };

    case actionTypes.ADD_ACHIEVEMENT:
      return {
        ...state,
        achievements: [...state.achievements, action.payload]
      };

    case actionTypes.RESET_STATE:
      return initialState;

    default:
      return state;
  }
}

// Create context
const TravelContext = createContext();

// Context provider component
export function TravelContextProvider({ children }) {
  const [state, dispatch] = useReducer(travelReducer, initialState);

  // Action creators
  const actions = {
    setSelectedPlace: (place) => {
      dispatch({ type: actionTypes.SET_SELECTED_PLACE, payload: place });
    },

    setGameSession: (session) => {
      dispatch({ type: actionTypes.SET_GAME_SESSION, payload: session });
    },

    updateUserPreferences: (preferences) => {
      dispatch({ type: actionTypes.UPDATE_USER_PREFERENCES, payload: preferences });
    },

    setSearchQuery: (query) => {
      dispatch({ type: actionTypes.SET_SEARCH_QUERY, payload: query });
    },

    setFilters: (filters) => {
      dispatch({ type: actionTypes.SET_FILTERS, payload: filters });
    },

    updateGameProgress: (gameId, progress) => {
      dispatch({
        type: actionTypes.UPDATE_GAME_PROGRESS,
        payload: { gameId, progress }
      });
    },

    addAchievement: (achievement) => {
      dispatch({ type: actionTypes.ADD_ACHIEVEMENT, payload: achievement });
    },

    resetState: () => {
      dispatch({ type: actionTypes.RESET_STATE });
    }
  };

  const contextValue = {
    ...state,
    ...actions
  };

  return (
    <TravelContext.Provider value={contextValue}>
      {children}
    </TravelContext.Provider>
  );
}

// Custom hook to use the Travel context
export function useTravelContext() {
  const context = useContext(TravelContext);
  if (!context) {
    throw new Error('useTravelContext must be used within a TravelContextProvider');
  }
  return context;
}

export default TravelContext;